import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Search, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { PAYARC_SIC_CODES, SicCode } from '@/data/sicCodes';

export interface DynamicSicCode {
  code: string;
  description: string;
  identifier?: string;
  type?: 'traditional' | 'payfac';
}

interface SicCodeSelectorProps {
  value?: string;
  valueDescription?: string;
  onValueChange: (sicCode: SicCode | null) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
  dynamicCodes?: DynamicSicCode[];
  loading?: boolean;
}

export function SicCodeSelector({
  value,
  valueDescription,
  onValueChange,
  placeholder = "Select SIC/MCC code",
  required = false,
  error,
  dynamicCodes,
  loading = false,
}: SicCodeSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Use dynamic codes only - no fallback to static codes
  const availableCodes = useMemo(() => {
    // If dynamicCodes is explicitly provided (even if empty), use it
    if (dynamicCodes !== undefined) {
      return dynamicCodes;
    }
    // Only use static codes if dynamicCodes is not provided at all
    return PAYARC_SIC_CODES;
  }, [dynamicCodes]);

  // Find selected SIC code - match both code AND description for duplicate codes
  const selectedCode = useMemo(() => {
    if (!value) return null;

    // If we have a description, use it to find exact match (for duplicate codes like 5732)
    if (valueDescription) {
      return availableCodes.find(code =>
        code.code === value && code.description === valueDescription
      ) || null;
    }

    // Otherwise just match by code (fallback for backward compatibility)
    return availableCodes.find(code => code.code === value) || null;
  }, [value, valueDescription, availableCodes]);

  // Get filtered SIC codes based on search
  const filteredCodes = useMemo(() => {
    if (!searchQuery) {
      // Sort by risk level: payfac (low-risk) first, then traditional (high-risk)
      // Note: "payfac" = standard merchant categories (low-risk), "traditional" = high-risk categories
      const sorted = [...availableCodes].sort((a, b) => {
        const aType = 'type' in a ? a.type : 'payfac';
        const bType = 'type' in b ? b.type : 'payfac';

        if (aType === bType) return 0;
        return aType === 'payfac' ? -1 : 1;
      });
      return sorted;
    }

    const query = searchQuery.toLowerCase().trim();

    // First, find exact code matches
    const exactCodeMatches = availableCodes.filter(code =>
      code.code.toLowerCase() === query
    );

    // Then, find partial matches in code, description, or identifier
    const partialMatches = availableCodes.filter(code => {
      // Skip if already in exact matches
      if (exactCodeMatches.some(exact => exact.code === code.code)) {
        return false;
      }

      const matchesCode = code.code.toLowerCase().includes(query);
      const matchesDescription = code.description.toLowerCase().includes(query);
      const matchesIdentifier = 'identifier' in code &&
        typeof code.identifier === 'string' &&
        code.identifier.toLowerCase().includes(query);

      return matchesCode || matchesDescription || matchesIdentifier;
    });

    // Sort each group by risk level: payfac (low-risk) first, then traditional (high-risk)
    // Note: "payfac" = standard merchant categories (low-risk), "traditional" = high-risk categories
    // Secondary sort by description for stable ordering when types are the same
    const sortByRisk = (codes: typeof availableCodes) => {
      return codes.sort((a, b) => {
        const aType = 'type' in a ? a.type : 'payfac';
        const bType = 'type' in b ? b.type : 'payfac';

        // Primary sort: payfac (low-risk) before traditional (high-risk)
        if (aType !== bType) {
          return aType === 'payfac' ? -1 : 1;
        }

        // Secondary sort: alphabetical by description for stable ordering
        return a.description.localeCompare(b.description);
      });
    };

    // Combine exact matches first (sorted by risk), then partial matches (sorted by risk)
    return [
      ...sortByRisk([...exactCodeMatches]),
      ...sortByRisk([...partialMatches])
    ];
  }, [searchQuery, availableCodes]);

  const handleSelect = (code: SicCode) => {
    onValueChange(code);
    setOpen(false);
    setSearchQuery('');
  };

  const handleClear = () => {
    onValueChange(null);
    setOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="space-y-2">
      <Label>
        SIC/MCC Code {required && <span className="text-red-500">*</span>}
      </Label>
      
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={loading}
            className={cn(
              "w-full justify-between",
              !selectedCode && "text-muted-foreground",
              error && "border-red-500"
            )}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading industry codes...
              </>
            ) : selectedCode ? (
              <span className="truncate">
                {selectedCode.code} - {selectedCode.description}
              </span>
            ) : (
              placeholder
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className="w-[600px] p-0" align="start">
          <div className="flex flex-col">
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                placeholder="Search SIC codes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                autoFocus
              />
            </div>
            
            <div className="max-h-[300px] overflow-y-auto">
              {availableCodes.length === 0 && !loading && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No industry codes available. Please contact support to configure codes for your account.
                </div>
              )}

              {availableCodes.length > 0 && filteredCodes.length === 0 && searchQuery && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No SIC codes found matching "{searchQuery}".
                </div>
              )}

              <div className="p-1">
                {selectedCode && availableCodes.length > 0 && (
                  <div
                    onClick={handleClear}
                    className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm text-red-600 hover:bg-accent hover:text-red-700"
                  >
                    Clear selection
                  </div>
                )}

                {filteredCodes.map((code, index) => {
                  const isSelected = selectedCode?.code === code.code &&
                    selectedCode?.description === code.description;

                  // Determine code type for color coding: green for payfac (low-risk), red for traditional (high-risk)
                  const codeType = 'type' in code ? code.type : 'payfac';
                  const isTraditional = codeType === 'traditional';

                  return (
                    <div
                      key={code.code +"_"+ index}
                      onClick={() => handleSelect(code)}
                      className={cn(
                        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm",
                        isSelected
                          ? "bg-blue-600 text-white"
                          : "hover:bg-accent hover:text-accent-foreground"
                      )}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          isSelected ? "opacity-100 text-white" : "opacity-0"
                        )}
                      />
                      <div className={cn(
                        "flex-1 truncate",
                        isSelected
                          ? "text-white"
                          : (isTraditional ? "text-red-600" : "text-green-600")
                      )}>
                        <span className="font-medium">{code.code}</span>
                        <span className="ml-2">
                          - {code.description}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}
